import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Pagination from '../../component/Admin/Pagination';
import { FaPlusCircle, FaTrashAlt, FaEdit, FaUser } from 'react-icons/fa';
import Swal from 'sweetalert2';
import TableBasic from '../../component/Admin/TableBasic';
import { canAccess } from '../../helper/permissions';

const Users = ({ data }) => {
    const navigate = useNavigate();
    const header = {
        headers: { Authorization: `Bearer ${data.token}` },
    };
    const permissions = data?.user?.permissions || {};
    const canCreate = canAccess(permissions, 'users', 'create');
    const canEdit = canAccess(permissions, 'users', 'edit');
    const canDelete = canAccess(permissions, 'users', 'delete');
    const [listUsers, setListUsers] = useState([]);
    const [meta, setMeta] = useState({});
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);

    useEffect(() => {
        getListUsers();
        // eslint-disable-next-line
    }, [page, limit]);

    const getListUsers = () => {
        axios
            .get(`api/admin?page=${page}&limit=${limit}`, header)
            .then((response) => {
                setMeta(response.data.meta);
                setListUsers(response.data.items);
            })
            .catch((error) => {
                console.error('Error fetching data:', error);
            });
    };

    const selectPage = (value) => setPage(value);
    const nextPage = () => {
        if (page < meta.totalPages) setPage(page + 1);
    };
    const prevPage = () => {
        if (page > 1) setPage(page - 1);
    };
    const limitPage = (event) => {
        setLimit(Number(event.target.value));
        setPage(1);
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Apakah Anda yakin?',
            text: 'Data ini akan dihapus!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#5C5E62',
            confirmButtonText: 'Ya, hapus!',
        }).then((result) => {
            if (!result.isConfirmed) return;
            axios
                .delete(`api/admin/${id}`, header)
                .then(() => {
                    getListUsers();
                    Swal.fire({
                        title: 'Sukses!',
                        text: 'Data berhasil Dihapus.',
                        icon: 'success',
                        timer: 1000,
                        showConfirmButton: true,
                    });
                })
                .catch(() => {
                    Swal.fire('Gagal!', 'Terjadi kesalahan saat menghapus data.', 'error');
                });
        });
    };

    const head = [
        { name: 'No.', align: 'center' },
        { name: 'User', align: 'left' },
        { name: 'Role', align: 'center' },
        { name: 'Journal', align: 'left' },
        { name: 'Action', align: 'center', sticky: true },
    ];

    return (
        <>
            {canCreate ? (
                <div className="mb-4">
                    <div className="card">
                        <div className="card-body toolbar-row !py-4 justify-end">
                            <button
                                onClick={() => navigate('/admin/users/create')}
                                className="btn-primary toolbar-action shrink-0"
                                type="button"
                            >
                                <FaPlusCircle />
                                <span>Tambah Users</span>
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}

            <TableBasic head={head}>
                {listUsers?.length ? (
                    listUsers.map((item, index) => {
                        const initial = (item.nama || item.username || '?')
                            .trim()
                            .charAt(0)
                            .toUpperCase();
                        const isSelf = item.id === data?.user?.id;

                        return (
                            <tr
                                key={item.id || index}
                                className="border-b border-gray-100 bg-white transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700/60"
                            >
                                <td className="text-center text-gray-500 dark:text-gray-400">
                                    {(page - 1) * limit + index + 1}
                                </td>

                                <td className="text-left">
                                    <div className="flex min-w-0 items-center gap-2.5">
                                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-xs font-bold text-primary dark:bg-primary/25 dark:text-primary-on-dark">
                                            {initial || <FaUser />}
                                        </div>
                                        <div className="min-w-0 text-left">
                                            <div className="truncate font-semibold text-gray-900 dark:text-gray-100">
                                                {item.nama || '-'}
                                            </div>
                                            <div className="truncate text-[11px] text-gray-500 dark:text-gray-400">
                                                @{item.username}
                                            </div>
                                        </div>
                                    </div>
                                </td>

                                <td className="text-center">
                                    <span
                                        className={`inline-flex rounded-md border px-2 py-0.5 text-[11px] font-semibold capitalize ${
                                            item.level === 'admin'
                                                ? 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                                                : 'border-primary/25 bg-primary/10 text-primary dark:border-primary dark:bg-primary/30 dark:text-primary-on-dark'
                                        }`}
                                    >
                                        {item.level || '-'}
                                    </span>
                                </td>

                                <td className="text-left">
                                    {item.level === 'admin' ? (
                                        <span className="text-[11px] text-gray-400 dark:text-gray-500">
                                            Semua journal
                                        </span>
                                    ) : item.journals?.length ? (
                                        <div className="flex max-w-md flex-wrap gap-1">
                                            {item.journals.slice(0, 4).map((j) => (
                                                <span
                                                    key={j.journal_id}
                                                    className="max-w-[140px] truncate rounded-md bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                                                    title={j.journal_name}
                                                >
                                                    {j.journal_name}
                                                </span>
                                            ))}
                                            {item.journals.length > 4 ? (
                                                <span className="rounded-md bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary dark:text-primary-on-dark">
                                                    +{item.journals.length - 4}
                                                </span>
                                            ) : null}
                                        </div>
                                    ) : (
                                        <span className="text-[11px] font-medium text-amber-600 dark:text-amber-400">
                                            Belum di-assign
                                        </span>
                                    )}
                                </td>

                                <td className="sticky right-0 z-10 whitespace-nowrap bg-white text-center shadow-[-6px_0_10px_-8px_rgba(0,0,0,0.35)] md:static md:shadow-none dark:bg-gray-800">
                                    <div className="inline-flex items-center gap-0.5 rounded-lg border border-gray-200 bg-gray-50 p-0.5 dark:border-gray-600 dark:bg-gray-700/60">
                                        {canEdit ? (
                                            <button
                                                type="button"
                                                className="flex items-center justify-center rounded-md text-link transition hover:bg-primary/10 dark:hover:bg-primary/30"
                                                onClick={() =>
                                                    navigate(`/admin/users/${item.id}/edit`)
                                                }
                                                title="Edit"
                                            >
                                                <FaEdit />
                                            </button>
                                        ) : null}
                                        {canDelete ? (
                                            <button
                                                type="button"
                                                disabled={isSelf}
                                                className="flex items-center justify-center rounded-md text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent dark:text-red-400 dark:hover:bg-red-900/30 dark:disabled:text-gray-600"
                                                onClick={() => handleDelete(item.id)}
                                                title={isSelf ? 'Tidak bisa hapus akun sendiri' : 'Hapus'}
                                            >
                                                <FaTrashAlt />
                                            </button>
                                        ) : null}
                                    </div>
                                </td>
                            </tr>
                        );
                    })
                ) : (
                    <tr className="bg-white dark:bg-gray-800">
                        <td colSpan={5} className="px-6 py-12">
                            <div className="flex justify-center">
                                <div className="text-center">
                                    <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-400 dark:bg-gray-700">
                                        <FaUser />
                                    </div>
                                    <div className="text-sm font-semibold text-gray-400 dark:text-gray-500">
                                        Data Tidak Ditemukan
                                    </div>
                                </div>
                            </div>
                        </td>
                    </tr>
                )}
            </TableBasic>

            <Pagination
                currentPage={page}
                totalPages={meta.totalPages}
                limit={limit}
                limitPage={limitPage}
                selectPage={selectPage}
                nextPage={nextPage}
                prevPage={prevPage}
            />
        </>
    );
};

export default Users;
